import React, { useState } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { isDarkTheme } from '@/constants/Colors';

export const getWarningLinkColor = theme => isDarkTheme(theme) ? '#ffd470' : '#7a5a10';

export function ChatInputBannerAction({ children, onPress, primary = false, disabled = false }) {
  const { colors } = useTheme();
  // Older translations include a text arrow; the shared action supplies its own.
  const label = typeof children === 'string' ? children.replace(/\s*→\s*$/, '') : children;
  const ink = primary ? colors.solid_color_primary : colors.text_color_primary;
  return <Pressable accessibilityRole="button" accessibilityLabel={typeof label === 'string' ? label : undefined} disabled={disabled} accessibilityState={{ disabled }}
    onPress={onPress} style={({ pressed }) => [styles.action, {
      backgroundColor: primary ? colors.text_color_primary : colors.solid_color_secondary,
      opacity: disabled ? 0.45 : pressed ? 0.65 : 1,
    }]}>
    <Text size="s" weight="semibold" style={{ color: ink, flexShrink: 1 }}>{label}</Text>
    <Icon name="chevron-right" size={10} color={ink} />
  </Pressable>;
}

// A separate, quiet card above the composer. All notices share the same neutral
// surface; a small status glyph carries severity without coloring the whole UI.
function ChatInputBanner({ children, title, actions, icon, color = 'danger', isCloseable = false, onClose,
  isDetached = false }) {
  const { colors, theme } = useTheme();
  const [dismissed, setDismissed] = useState(false);
  const [wide, setWide] = useState(false);
  if (dismissed) return null;
  const statusIcon = icon || ({ danger: 'circle-exclamation', warning: 'circle-exclamation',
    upgrade: 'sparkles', success: 'circle-check' }[color] || 'circle-info');
  const iconColor = color === 'danger' ? colors.color_destructive : color === 'warning'
    ? getWarningLinkColor(theme) : colors.text_color_secondary;
  return <View style={[styles.container, isDetached && styles.detached]} onLayout={event => setWide(event.nativeEvent.layout.width >= 600)}>
    <View style={[styles.card, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
      <View style={[styles.icon, { backgroundColor: colors.solid_color_secondary }]}>
        <Icon name={statusIcon} size={18} color={iconColor} />
      </View>
      <View style={[styles.content, wide && styles.wideContent]}>
        <View style={styles.copy}>
          {!!title && <Text size="n" weight="bold" style={styles.title}>{title}</Text>}
          <View style={styles.inlineContent}>
            {React.Children.map(children, child => typeof child === 'string' || typeof child === 'number'
              ? <Text size="s" color="secondary" style={styles.message}>{child}</Text> : child)}
          </View>
        </View>
        {!!actions && <View style={styles.actions}>{actions}</View>}
      </View>
      {isCloseable && <Pressable accessibilityRole="button" accessibilityLabel="Dismiss notification"
        onPress={() => { setDismissed(true); onClose?.(); }}
        style={({ pressed }) => [styles.close, { backgroundColor: pressed ? colors.solid_color_secondary : 'transparent' }]}>
        <Icon name="xmark" size={12} color={colors.text_color_secondary} />
      </Pressable>}
    </View>
  </View>;
}

const styles = StyleSheet.create({
  container: { width: '100%', marginBottom: 10 },
  detached: { paddingHorizontal: 10 },
  card: { flexDirection: 'row', alignItems: 'flex-start', padding: 14, gap: 12, borderRadius: 18, borderWidth: StyleSheet.hairlineWidth },
  icon: { width: 36, height: 36, borderRadius: 12, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  content: { flex: 1, minWidth: 0, gap: 12, paddingVertical: 1 },
  wideContent: { flexDirection: 'row', alignItems: 'center', gap: 18 },
  copy: { flex: 1, minWidth: 0, gap: 4 },
  title: { lineHeight: 20 },
  inlineContent: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  message: { lineHeight: 19, flexShrink: 1 },
  actions: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, flexShrink: 1 },
  action: { minHeight: 36, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 9, borderRadius: 18, paddingHorizontal: 14, paddingVertical: 8, maxWidth: '100%' },
  close: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', margin: -4 },
});

export default React.memo(ChatInputBanner);
